import { createContext, useContext, useEffect, useState, ReactNode } from 'react';

type ColorScheme = 'theme-a' | 'theme-b';
type Mode = 'light' | 'dark' | 'system';

interface ThemeState {
  colorScheme: ColorScheme;
  mode: Mode;
  effectiveMode: 'light' | 'dark';
}

interface ThemeContextValue extends ThemeState {
  setColorScheme: (scheme: ColorScheme) => void;
  setMode: (mode: Mode) => void;
  toggleMode: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

// LocalStorage keys
const STORAGE_KEYS = {
  COLOR_SCHEME: 'pilot-app-color-scheme',
  MODE: 'pilot-app-mode',
};

export function ThemeProvider({ children }: { children: ReactNode }) {
  // Initialize from localStorage or defaults
  const [colorScheme, setColorSchemeState] = useState<ColorScheme>(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.COLOR_SCHEME);
    return (stored === 'theme-a' || stored === 'theme-b') ? stored : 'theme-a';
  });

  const [mode, setModeState] = useState<Mode>(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.MODE);
    return (stored === 'light' || stored === 'dark' || stored === 'system') ? stored : 'system';
  });

  // Resolve system preference
  const [systemPreference, setSystemPreference] = useState<'light' | 'dark'>(() => {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  // Calculate effective mode
  const effectiveMode = mode === 'system' ? systemPreference : mode;

  // Listen to system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handler = (e: MediaQueryListEvent) => {
      setSystemPreference(e.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  // Apply theme to DOM
  useEffect(() => {
    const root = document.documentElement;

    // Add transitioning class to prevent flash
    root.classList.add('theme-transitioning');

    // Set data attributes
    root.setAttribute('data-color-scheme', colorScheme);
    root.setAttribute('data-mode', effectiveMode);

    // Remove transitioning class after a frame
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        root.classList.remove('theme-transitioning');
      });
    });
  }, [colorScheme, effectiveMode]);

  // Persist to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.COLOR_SCHEME, colorScheme);
  }, [colorScheme]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.MODE, mode);
  }, [mode]);

  const setColorScheme = (scheme: ColorScheme) => {
    setColorSchemeState(scheme);
  };

  const setMode = (newMode: Mode) => {
    setModeState(newMode);
  };

  const toggleMode = () => {
    if (mode === 'system') {
      // If system, toggle to opposite of system preference
      setModeState(systemPreference === 'dark' ? 'light' : 'dark');
    } else {
      // Toggle between light and dark
      setModeState(mode === 'dark' ? 'light' : 'dark');
    }
  };

  return (
    <ThemeContext.Provider
      value={{
        colorScheme,
        mode,
        effectiveMode,
        setColorScheme,
        setMode,
        toggleMode,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}
